import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MaterialModule } from 'src/app/material/material.module';
import { FormsModule as SharedFormsModule } from 'src/app/shared/components/forms/forms.module';
import { EvolutionForm, FormValues } from 'src/app/shared/models/evolutionForm';
import { mapFormData } from '../form-mapper';
import { WizardField, buildFields, fromControlValue, isEmptyValue, toControlValue } from '../form-fields';

@Component({
    selector: 'app-json-form-wizard',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MaterialModule, SharedFormsModule],
    templateUrl: './json-form-wizard.component.html',
    styleUrls: ['./json-form-wizard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JsonFormWizardComponent implements OnInit {
    @Input({ required: true }) form: EvolutionForm;
    @Input() initialValues: FormValues | null = null;
    @Input() showCancel = true;
    @Output() submitted = new EventEmitter<{ formType: string; values: FormValues }>();
    @Output() cancelled = new EventEmitter<void>();

    sections: EvolutionForm['formSections'] = [];
    step = 0;
    schema: any;
    fields: WizardField[] = [];
    group: FormGroup;
    todayStartDate: Date = new Date();

    private values: FormValues = {};
    private previousStep = new Map<number, number>();
    private readonly dateSuffix = '__date';
    private readonly timeSuffix = '__time';

    constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef) { }

    get isLast(): boolean {
        return this.sections[this.step]?.final === 1 || this.step === this.sections.length - 1;
    }

    get canGoBack(): boolean {
        return this.step > 0;
    }

    get canGoNext(): boolean {
        return !this.isLast;
    }

    ngOnInit(): void {
        this.sections = mapFormData(this.form).formSections;
        this.values = this.initialValues ? JSON.parse(JSON.stringify(this.initialValues)) : {};
        this.buildStep();
    }

    next(): void {
        if (!this.validateStep()) return;
        this.saveStepValues();
        const target = this.resolveNextStep();
        this.previousStep.set(target, this.step);
        this.step = target;
        this.buildStep();
    }

    back(): void {
        this.saveStepValues();
        this.step = this.previousStep.get(this.step) ?? Math.max(0, this.step - 1);
        this.buildStep();
    }

    submit(): void {
        if (!this.validateStep()) return;
        this.saveStepValues();
        this.submitted.emit({ formType: this.schema?.title, values: this.keepVisitedSteps() });
    }

    reset(): void {
        this.values = {};
        this.previousStep.clear();
        this.step = 0;
        this.buildStep();
    }

    // Current answers without validation, for persisting an unfinished form.
    getDraft(): { formType: string; values: FormValues } {
        this.saveStepValues();
        return { formType: this.schema?.title, values: this.keepVisitedSteps() };
    }

    hasError(field: WizardField): boolean {
        if (field.kind === 'datetime') {
            const dateControl = this.group.get(this.datetimeDateKey(field.key));
            const timeControl = this.group.get(this.datetimeTimeKey(field.key));
            return !!(
                (dateControl && dateControl.invalid && dateControl.touched) ||
                (timeControl && timeControl.invalid && timeControl.touched)
            );
        }

        const control = this.group.get(field.key);
        return !!control && control.invalid && control.touched;
    }

    errorText(field: WizardField): string {
        if (field.kind === 'datetime') {
            const dateControl = this.group.get(this.datetimeDateKey(field.key));
            const timeControl = this.group.get(this.datetimeTimeKey(field.key));
            if (dateControl?.errors?.['required'] || timeControl?.errors?.['required']) return 'Campo requerido';
            return 'Valor inválido';
        }

        const errors = this.group.get(field.key)?.errors ?? {};
        if (errors['required']) return 'Campo requerido';
        if (errors['minlength']) return `Mínimo ${errors['minlength'].requiredLength} caracteres`;
        if (errors['maxlength']) return `Máximo ${errors['maxlength'].requiredLength} caracteres`;
        if (errors['min']) return `Valor mínimo ${errors['min'].min}`;
        if (errors['max']) return `Valor máximo ${errors['max'].max}`;
        return 'Valor inválido';
    }

    private buildStep(): void {
        this.schema = this.sections[this.step]?.formData ?? { properties: {} };
        this.fields = buildFields(this.schema);
        const controls = {};
        this.fields.forEach(field => {
            const stored = this.values[field.key]?.value ?? field.default;
            const controlValue = toControlValue(field.kind, stored);
            controls[field.key] = [controlValue, field.kind === 'datetime' ? [] : field.validators];

            if (field.kind === 'datetime') {
                const datePart = this.getDatetimeDatePart(controlValue);
                const timePart = this.getDatetimeTimePart(controlValue);
                const required = field.required ? [Validators.required] : [];
                controls[this.datetimeDateKey(field.key)] = [datePart, required];
                controls[this.datetimeTimeKey(field.key)] = [timePart, required];
            }
        });
        this.group = this.fb.group(controls);
        this.cdr.markForCheck();
    }

    private validateStep(): boolean {
        if (this.group.valid) return true;
        this.group.markAllAsTouched();
        this.cdr.markForCheck();
        return false;
    }

    private saveStepValues(): void {
        const raw = this.group.getRawValue();
        this.fields.forEach(field => {
            let sourceValue = raw[field.key];
            if (field.kind === 'datetime') {
                const datePart = raw[this.datetimeDateKey(field.key)];
                const timePart = raw[this.datetimeTimeKey(field.key)];
                sourceValue = this.composeDatetime(datePart, timePart);
                this.group.get(field.key)?.setValue(sourceValue, { emitEvent: false });
            }

            const value = fromControlValue(field.kind, sourceValue);
            if (isEmptyValue(value)) {
                delete this.values[field.key];
                return;
            }
            this.values[field.key] = {
                step: { number: this.step, title: this.schema?.title },
                title: field.summaryTitle,
                values: field.options.map(option => option.value),
                goTo: field.goTo,
                value,
            };
        });
    }

    private resolveNextStep(): number {
        const branchField = this.fields.find(field => field.goTo?.length);
        if (branchField) {
            const index = branchField.options.findIndex(option => option.value === this.group.value[branchField.key]);
            const target = branchField.goTo[index];
            if (Number.isInteger(target)) return target - 1;
        }
        return this.step + 1;
    }

    datetimeDateKey(key: string): string {
        return `${key}${this.dateSuffix}`;
    }

    datetimeTimeKey(key: string): string {
        return `${key}${this.timeSuffix}`;
    }

    private getDatetimeDatePart(value: any): Date | null {
        if (!value || typeof value !== 'string') return null;
        const [date] = value.split('T');
        if (!date) return null;

        const [day, month, year] = date.split('-');
        if (!day || !month || !year) return null;

        const result = new Date(Number(year), Number(month) - 1, Number(day));
        return isNaN(result.getTime()) ? null : result;
    }

    private getDatetimeTimePart(value: any): string | null {
        if (!value || typeof value !== 'string') return null;
        const [, time] = value.split('T');
        if (!time) return null;
        return time.slice(0, 5);
    }

    private composeDatetime(datePart: Date | null, timePart: string | null): string | null {
        if (!datePart || !timePart) return null;

        const year = datePart.getFullYear().toString().padStart(4, '0');
        const month = (datePart.getMonth() + 1).toString().padStart(2, '0');
        const day = datePart.getDate().toString().padStart(2, '0');
        return `${day}-${month}-${year}T${timePart}`;
    }

    // Drops answers from branches the user abandoned after changing a goTo answer.
    private keepVisitedSteps(): FormValues {
        const visited = new Set<number>([this.step]);
        let current = this.step;
        while (this.previousStep.has(current) && !visited.has(this.previousStep.get(current))) {
            current = this.previousStep.get(current);
            visited.add(current);
        }
        visited.add(0);
        return Object.fromEntries(Object.entries(this.values).filter(([, item]) => visited.has(item.step.number)));
    }
}
